import React, { useMemo, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { registerServiceWorker } from './utils/serviceWorker';
import { initializePerformanceMonitoring } from './utils/performance';

import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import { AuthProvider, AuthProtectedRoute } from './components/Auth';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import ResponsiveAppBar from './components/Navbar'

import { GarageLocationProvider, GarageLocationProtectedRoute } from './components/Location';
import { SmartCarNumberProvider } from './components/Smartcars';
import { ValidateAccess } from './components/Access';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingFallback, { ComponentPreloader } from './components/LazyLoader';

// Immediate load for critical pages
import NoPage from "./pages/NoPage";
import Home from "./pages/Home";

// Lazy load heavy components for better performance
const Smartcar = lazy(() => import("./pages/Smartcar"));
const SmartcarStreaming = lazy(() => import("./pages/SmartcarStreaming"));
const Sotefin = lazy(() => import("./pages/Sotefin"));
const Reeta = lazy(() => import("./pages/Reeta"));
const Reports = lazy(() => import("./pages/Reports"));
const Media = lazy(() => import("./pages/Media"));
const NotificationManagement = lazy(() => import("./pages/NotificationManagement"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const UserManagementUI = lazy(() => import("./pages/UserManagementUI"));
const ApiConsole = lazy(() => import("./pages/APIConsole"));

type PageComponentsType = {
  [key: string]: () => JSX.Element;
};

const PageComponents: PageComponentsType =  {
  Smartcar: () => (
    <Suspense fallback={<LoadingFallback message="Loading Smartcar Control..." showSkeleton skeletonType="page" />}>
      <Smartcar />
    </Suspense>
  ),
  Sotefin: () => (
    <Suspense fallback={<LoadingFallback message="Loading Sotefin Management..." showSkeleton skeletonType="page" />}>
      <Sotefin />
    </Suspense>
  ),
  Reeta: () => (
    <Suspense fallback={<LoadingFallback message="Loading Reeta Interface..." showSkeleton skeletonType="page" />}>
      <Reeta />
    </Suspense>
  ),
  Reports: () => (
    <Suspense fallback={<LoadingFallback message="Loading Reports..." showSkeleton skeletonType="page" />}>
      <Reports />
    </Suspense>
  ),
  Media: () => (
    <Suspense fallback={<LoadingFallback message="Loading Media Management..." showSkeleton skeletonType="page" />}>
      <Media />
    </Suspense>
  ),
  Notification_Management: () => (
    <Suspense fallback={<LoadingFallback message="Loading Notification Management..." showSkeleton skeletonType="page" />}>
      <NotificationManagement />
    </Suspense>
  ),
  Onboarding: () => (
    <Suspense fallback={<LoadingFallback message="Loading Onboarding..." showSkeleton skeletonType="page" />}>
      <Onboarding />
    </Suspense>
  ),
  User_Management: () => (
    <Suspense fallback={<LoadingFallback message="Loading User Management..." showSkeleton skeletonType="page" />}>
      <UserManagementUI />
    </Suspense>
  ),
  API_Console: () => (
    <Suspense fallback={<LoadingFallback message="Loading API Console..." showSkeleton skeletonType="page" />}>
      <ApiConsole />
    </Suspense>
  ),
}

const pages = Object.keys(PageComponents);

const createSettings = (logout: () => void) => [
  {
    title: 'Profile',
    onClick: () => console.log('Profile')
  },
  {
    title: 'Account',
    onClick: () => console.log('Account')
  },
  {
    title: 'Dashboard',
    onClick: () => console.log('Dashboard')
  },
  {
    title: 'Logout',
    onClick: () => {
      console.log('logout called')
      localStorage.removeItem('NRBA_SESSION')
      logout()
    }
  }
];

function AppContent() {
  const { logout } = useAuth0();
  const settings = useMemo(() => createSettings(logout), [logout]);
  const location = useLocation();
  
  // Define pages where location dropdown should be hidden
  const pagesWithoutLocationDropdown = ['/user-management', '/api-console'];
  const hideLocationDropdown = pagesWithoutLocationDropdown.includes(location.pathname);
  
  // Preload critical components on app start
  React.useEffect(() => {
    // Preload most commonly used components
    ComponentPreloader.preload(() => import("./pages/Smartcar"), 'Smartcar');
    ComponentPreloader.preload(() => import("./pages/Sotefin"), 'Sotefin');
  }, []);
  
  return (
    <div className="App">
      <ResponsiveAppBar
        pages={pages}
        settings={settings}
        hideLocationDropdown={hideLocationDropdown}
      />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="*" element={<NoPage />} />
        {pages.map((page) => {
          const PageComponent = PageComponents[page]
          return (
            <Route
              key={`/${page.toLowerCase().replace('_', '-')}`}
              path={`/${page.toLowerCase().replace('_', '-')}`}
              element={
                <AuthProtectedRoute element={
                  <ValidateAccess permission={`page:${page.toLowerCase()}`.replace('smartcar', 'smartcar_control')}>
                    <GarageLocationProtectedRoute element={<PageComponent />} />
                  </ValidateAccess>
                } />
              }
            />
          )
          })};

        {/* SmartcarStreaming route - hidden from navbar */}
        <Route
          path="/smartcar-streaming"
          element={
            <AuthProtectedRoute element={
              <ValidateAccess permission="page:smartcar_control">
                <GarageLocationProtectedRoute element={
                  <Suspense fallback={<LoadingFallback message="Loading Smartcar Streaming..." showSkeleton skeletonType="page" />}>
                    <SmartcarStreaming />
                  </Suspense>
                } />
              </ValidateAccess>
            } />
          }
        />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

console.log('your audience is', process.env.REACT_APP_AUTH0_AUDIENCE)
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <Auth0Provider
        domain={process.env.REACT_APP_AUTH0_DOMAIN as string}
        clientId={process.env.REACT_APP_AUTH0_CLIENT_ID as string}
        useRefreshTokens={true}
        cacheLocation='localstorage'
        authorizationParams={{
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          redirect_uri: `${window.location.origin}`,
          scope: process.env.REACT_APP_AUTH0_SCOPE,
        }}
      >
        <AuthProvider>
          <GarageLocationProvider>
            <SmartCarNumberProvider>
                <App />
            </SmartCarNumberProvider>
          </GarageLocationProvider>
        </AuthProvider>
      </Auth0Provider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Initialize performance monitoring
initializePerformanceMonitoring();

// Register service worker for offline functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    registerServiceWorker();
  });
}
